// Pure helpers every page shares to read a metric and a visual: dates, the
// change over a period, a figure split into number and unit, the scale a
// value is read on, and what an axis of labels is. Nothing here invents a
// number; every figure is read from the API payload.
import type { MetricValue, Overview, Stage, Visual } from "@dashboard-intelligent/api/src/modules/industry/types";
import { formatMetric } from "../../format";

const dateFormat = new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

/** "1 Januari 2025" from an ISO date; the raw string when it does not parse. */
export function formatDate(iso: string): string {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? iso : dateFormat.format(new Date(time));
}

export function formatPeriod(start: string, end: string): string {
  return `${formatDate(start)} sampai ${formatDate(end)}`;
}

/** A metric's history in words: how many periods, from which to which, from what value to what value. */
export function trendLabel(metric: MetricValue): string {
  const points = metric.trend;
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return `${metric.label}: tidak ada data tren`;
  return `${metric.label}, ${points.length} periode dari ${first.label} ke ${last.label}: ${formatMetric(first.value, metric.format)} menjadi ${formatMetric(last.value, metric.format)}`;
}

/** Why the value is missing, the rule it was graded by, or why it was not graded. Null when there is nothing to add. */
export function metricSupport(metric: MetricValue): string | null {
  if (metric.value === null) return metric.note ?? "Data belum bisa menghasilkan nilai ini.";
  const parts = [metric.target?.basis, metric.note].filter((p): p is string => typeof p === "string" && p.length > 0);
  return parts.length > 0 ? parts.join(" ") : null;
}

/** The metrics of a stage, in the stage's own metricIds order; unknown ids are skipped. */
export function stageMetrics(stage: Stage, metrics: MetricValue[]): MetricValue[] {
  const byId = new Map(metrics.map((m) => [m.id, m]));
  return stage.metricIds.map((id) => byId.get(id)).filter((m): m is MetricValue => m !== undefined);
}

/** A tenant whose definitions have not landed: nothing computed, nothing to draw. */
export function isEmptyOverview(overview: Overview): boolean {
  return (
    overview.northStar.metric === null &&
    overview.northStar.components.length === 0 &&
    overview.headline.length === 0 &&
    overview.stages.length === 0 &&
    overview.metrics.length === 0 &&
    overview.visuals.length === 0
  );
}

export type Target = NonNullable<MetricValue["target"]>;
export type Graded = MetricValue & { target: Target; value: number };

export function isGraded(metric: MetricValue): metric is Graded {
  return metric.target !== null && metric.value !== null;
}

const one = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 });
const plain = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 });
const two = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 });

function scaled(abs: number): { n: number; unit: string } | null {
  if (abs >= 1e12) return { n: abs / 1e12, unit: "T" };
  if (abs >= 1e9) return { n: abs / 1e9, unit: "M" };
  if (abs >= 1e6) return { n: abs / 1e6, unit: "jt" };
  return null;
}

/** A figure split into the number and its unit, for a card that sets the unit small. */
export function figureParts(value: number | null, format: MetricValue["format"]): { value: string; unit?: string } {
  if (value === null || !Number.isFinite(value)) return { value: "Belum terhitung" };
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  switch (format) {
    case "idr": {
      const big = scaled(abs);
      return big ? { value: `${sign}Rp ${one.format(big.n)}`, unit: big.unit } : { value: `${sign}Rp ${plain.format(abs)}` };
    }
    case "count": {
      const big = scaled(abs);
      return big ? { value: `${sign}${one.format(big.n)}`, unit: big.unit } : { value: `${sign}${plain.format(abs)}` };
    }
    case "percent":
      return { value: one.format(value), unit: "%" };
    case "minutes":
      return { value: one.format(value), unit: "menit" };
    case "hours":
      return { value: one.format(value), unit: "jam" };
    case "days":
      return { value: one.format(value), unit: "hari" };
    case "decimal":
      return { value: two.format(value) };
    case "score":
      return { value: one.format(value) };
  }
}

export interface Movement {
  direction: "up" | "down" | "flat";
  /** "Naik 2,5 poin", "Turun 14,5%", "Tidak berubah". */
  size: string;
  /** "Jan 2026 ke Sep 2026". */
  window: string;
}

/**
 * The change across the metric's own window, first period to last. A
 * percentage moves in points; everything else in percent of where it
 * started. Null with no history, or one that starts at zero.
 */
export function movement(metric: MetricValue): Movement | null {
  const first = metric.trend[0];
  const last = metric.trend[metric.trend.length - 1];
  if (!first || !last || first === last) return null;
  const points = metric.format === "percent";
  const base = Math.abs(first.value);
  if (!points && base === 0) return null;
  const raw = points ? last.value - first.value : ((last.value - first.value) / base) * 100;
  const size = Math.round(Math.abs(raw) * 10) / 10;
  const window = `${first.label} ke ${last.label}`;
  if (size === 0) return { direction: "flat", size: "Tidak berubah", window };
  return { direction: raw > 0 ? "up" : "down", size: `${raw > 0 ? "Naik" : "Turun"} ${one.format(size)}${points ? " poin" : "%"}`, window };
}

/** A scale end that holds every value, rounded up to a 1, 2, or 5 step. */
export function roundScale(values: number[]): number {
  const top = Math.max(...values, 0);
  if (top <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(top));
  for (const step of [1, 2, 5, 10]) {
    if (top <= step * power) return step * power;
  }
  return 10 * power;
}

/** The scale a graded metric is read on: 0 to 100 for a percentage, else from zero to a round end over value and thresholds. */
export function gradedScale(metric: Graded): number | null {
  const numbers = [metric.value, metric.target.target, metric.target.warning, metric.target.critical];
  if (numbers.some((n) => !Number.isFinite(n) || n < 0)) return null;
  if (metric.format === "percent" && numbers.every((n) => n <= 100)) return 100;
  return roundScale(numbers.map((n) => n * 1.1));
}

/** A period label: a month, a quarter, a year, an hour. */
const PERIOD_LABEL = /^(\d{4}-Q[1-4]|Q[1-4] \d{4}|\d{4}-\d{2}|\d{4}|(Jan|Feb|Mar|Apr|Mei|Jun|Jul|Agu|Ags|Sep|Okt|Nov|Des)( \d{4})?|[0-2]?\d([.:][0-5]\d)?)$/;

/** Every category is a period, so the axis is time. */
export function isPeriodAxis(data: { label: string }[]): boolean {
  return data.length >= 2 && data.every((d) => d.label.length <= 8 && PERIOD_LABEL.test(d.label.trim()));
}

const WEEKDAY = /^(Sen|Sel|Rab|Kam|Jum|Sab|Min|Senin|Selasa|Rabu|Kamis|Jumat|Sabtu|Minggu)$/i;

/** Every category is a step on an ordered scale that is not time (histogram buckets, weekdays). */
export function isBucketAxis(data: { label: string }[]): boolean {
  return data.length >= 3 && (data.every((d) => /\d/.test(d.label)) || data.every((d) => WEEKDAY.test(d.label.trim())));
}

/** The longest single word of any label, in characters: a column name wraps at words, never inside one. */
export function longestWord(data: { label: string }[]): number {
  return data.reduce((m, d) => Math.max(m, ...d.label.split(/\s+/).map((w) => w.length)), 0);
}

/** Every value of a categorical visual is zero or more. */
export function nonNegative(visual: Visual): boolean {
  if (visual.kind === "trend") return visual.data.every((row) => visual.series.every((s) => Number(row[s.key]) >= 0));
  return visual.data.every((d) => d.value >= 0);
}
